import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '@/styles/permissionsStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * PropertyGroupsPage Component
 * 
 * Allows administrators to create and manage property groups for easier access control.
 * Property groups enable assigning multiple properties to users/roles at once.
 * Now includes template management for quick setup.
 */
const PropertyGroupsPage = () => {
    const { apiFetch, user } = useAuth();
    const [propertyGroups, setPropertyGroups] = useState([]);
    const [properties, setProperties] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Form state for creating/editing groups
    const [formData, setFormData] = useState({
        groupName: '',
        description: '',
        isActive: true
    });

    // State for managing properties in a group
    const [selectedGroupProperties, setSelectedGroupProperties] = useState([]);
    // Commented out unused state - keeping for potential future use
    // const [availableProperties, setAvailableProperties] = useState([]);
    const [showPropertiesModal, setShowPropertiesModal] = useState(false);
    const [propertySearchTerm, setPropertySearchTerm] = useState('');

    useEffect(() => {
        fetchPropertyGroups();
        fetchAllProperties();
        fetchTemplates();
    }, []);

    /**
     * Fetches property group templates.
     */
    const fetchTemplates = async () => {
        try {
            const response = await apiFetch('/api/Property/groups/templates');
            const data = await response.json();
            if (data.success) {
                setTemplates(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching templates:', error);
        }
    };

    /**
     * Fetches all property groups from the API.
     */
    const fetchPropertyGroups = async () => {
        setIsLoading(true);
        try {
            const response = await apiFetch('/api/Property/groups/all?includeInactive=true');
            const data = await response.json();
            if (data.success) {
                setPropertyGroups(data.data || []);
            } else {
                toast.error(data.message || 'Failed to load property groups');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching property groups:', error);
            toast.error('Failed to load property groups');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Fetches all available properties.
     */
    const fetchAllProperties = async () => {
        try {
            const response = await apiFetch('/api/Property/all');
            const data = await response.json();
            if (data.success) {
                setProperties(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching properties:', error);
        }
    };

    /**
     * Fetches properties assigned to a specific group.
     */
    const fetchGroupProperties = async (groupID) => {
        try {
            const response = await apiFetch(`/api/Property/groups/${groupID}/properties`);
            const data = await response.json();
            if (data.success) {
                setSelectedGroupProperties(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching group properties:', error);
        }
    };

    /**
     * Handles creating a new property group.
     */
    const handleCreateGroup = async () => {
        if (!formData.groupName.trim()) {
            toast.error('Group name is required');
            return;
        }

        try {
            const response = await apiFetch('/api/Property/groups/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    groupName: formData.groupName,
                    description: formData.description,
                    userID: user.UserID
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Property group created successfully');
                fetchPropertyGroups();
                resetForm();
            } else {
                toast.error(data.message || 'Failed to create property group');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating property group:', error);
            toast.error('Failed to create property group');
        }
    };

    /**
     * Handles updating an existing property group.
     */
    const handleUpdateGroup = async () => {
        if (!formData.groupName.trim()) {
            toast.error('Group name is required');
            return;
        }

        try {
            const response = await apiFetch('/api/Property/groups/update', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    propertyGroupID: selectedGroup.propertyGroupID,
                    groupName: formData.groupName,
                    description: formData.description,
                    isActive: formData.isActive,
                    userID: user.UserID
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Property group updated successfully');
                fetchPropertyGroups();
                resetForm();
            } else {
                toast.error(data.message || 'Failed to update property group');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error updating property group:', error);
            toast.error('Failed to update property group');
        }
    };

    /**
     * Handles deleting (deactivating) a property group.
     */
    const handleDeleteGroup = async (groupID) => {
        if (!confirm('Are you sure you want to delete this property group? This will deactivate the group.')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/Property/groups/delete/${groupID}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Property group deleted successfully');
                fetchPropertyGroups();
            } else {
                toast.error(data.message || 'Failed to delete property group');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting property group:', error);
            toast.error('Failed to delete property group');
        }
    };

    /**
     * Opens the modal to manage properties for a group.
     */
    const openPropertiesModal = async (group) => {
        setSelectedGroup(group);
        await fetchGroupProperties(group.propertyGroupID);
        setShowPropertiesModal(true);
    };

    /**
     * Handles saving property assignments for a group.
     */
    const handleSaveGroupProperties = async () => {
        if (!selectedGroup) return;

        const selectedPropertyIDs = selectedGroupProperties.map(p => p.propertyID);

        try {
            const response = await apiFetch('/api/Property/groups/assign-properties', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    propertyGroupID: selectedGroup.propertyGroupID,
                    propertyIDs: selectedPropertyIDs,
                    userID: user.UserID
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Properties assigned successfully');
                setShowPropertiesModal(false);
                fetchPropertyGroups();
            } else {
                toast.error(data.message || 'Failed to assign properties');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error assigning properties:', error);
            toast.error('Failed to assign properties');
        }
    };

    /**
     * Toggles a property in the selected group properties list.
     */
    const toggleProperty = (property) => {
        const isSelected = selectedGroupProperties.some(p => p.propertyID === property.propertyID);
        if (isSelected) {
            setSelectedGroupProperties(selectedGroupProperties.filter(p => p.propertyID !== property.propertyID));
        } else {
            setSelectedGroupProperties([...selectedGroupProperties, property]);
        }
    };

    /**
     * Resets the form state.
     */
    const resetForm = () => {
        setFormData({ groupName: '', description: '', isActive: true });
        setIsCreating(false);
        setIsEditing(false);
        setSelectedGroup(null);
        setSelectedTemplate(null);
    };

    /**
     * Handles creating a group from a template.
     */
    const handleCreateFromTemplate = async () => {
        if (!selectedTemplate || !formData.groupName.trim()) {
            toast.error('Please select a template and provide a group name');
            return;
        }

        try {
            const response = await apiFetch('/api/Property/groups/create-from-template', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    templateID: selectedTemplate.templateID,
                    groupName: formData.groupName,
                    description: formData.description,
                    userID: user.UserID
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Property group created from template successfully');
                fetchPropertyGroups();
                setShowTemplateModal(false);
                resetForm();
            } else {
                toast.error(data.message || 'Failed to create group from template');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating group from template:', error);
            toast.error('Failed to create group from template');
        }
    };

    /**
     * Opens the template selection modal.
     */
    const openTemplateModal = () => {
        if (templates.length === 0) {
            toast.info('No templates available');
            return;
        }
        setShowTemplateModal(true);
    };

    /**
     * Starts editing a group.
     */
    const startEdit = (group) => {
        setSelectedGroup(group);
        setFormData({
            groupName: group.groupName,
            description: group.description || '',
            isActive: group.isActive
        });
        setIsEditing(true);
        setIsCreating(false);
    };

    // Filter property groups based on search term
    const filteredGroups = propertyGroups.filter(group => 
        group.groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Filter available properties for the modal
    const filteredAvailableProperties = properties.filter(prop =>
        (prop.propertyName && prop.propertyName.toLowerCase().includes(propertySearchTerm.toLowerCase())) ||
        (prop.comparativeName && prop.comparativeName.toLowerCase().includes(propertySearchTerm.toLowerCase()))
    );

    return (
        <div className="permissions-container">
<div className="permissions-header">
                <h1>Property Groups Management</h1>
                <p>Organize properties into groups for easier access control</p>
            </div>

            <div className="permissions-content">
                {/* Create/Edit Form */}
                {(isCreating || isEditing) && (
                    <div className="form-card">
                        <h2>{isEditing ? 'Edit Property Group' : 'Create New Property Group'}</h2>
                        <div className="form-group">
                            <label>Group Name *</label>
                            <input
                                type="text"
                                value={formData.groupName}
                                onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                                placeholder="e.g., Regional Malls, Western Cape Properties"
                            />
                        </div>
                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Optional description of this property group"
                                rows="3"
                            />
                        </div>
                        {isEditing && (
                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                    <span style={{ marginLeft: '8px' }}>Active</span>
                                </label>
                            </div>
                        )}
                        <div className="form-actions">
                            <button 
                                onClick={isEditing ? handleUpdateGroup : handleCreateGroup}
                                className="btn-primary"
                            >
                                {isEditing ? 'Update Group' : 'Create Group'}
                            </button>
                            <button onClick={resetForm} className="btn-secondary">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Property Groups List */}
                {!isCreating && !isEditing && (
                    <>
                        <div className="list-header">
                            <div className="button-group">
                                <button onClick={() => setIsCreating(true)} className="btn-primary">
                                    + Create Property Group
                                </button>
                                {templates.length > 0 && (
                                    <button onClick={openTemplateModal} className="btn-secondary">
                                        📋 Use Template
                                    </button>
                                )}
                            </div>
                            <input
                                type="text"
                                placeholder="Search property groups..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        {isLoading ? (
                            <p>Loading...</p>
                        ) : filteredGroups.length === 0 ? (
                            <p>No property groups found.</p>
                        ) : (
                            <table className="permissions-table">
                                <thead>
                                    <tr>
                                        <th>Group Name</th>
                                        <th>Description</th>
                                        <th>Properties</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredGroups.map((group) => (
                                        <tr key={group.propertyGroupID}>
                                            <td><strong>{group.groupName}</strong></td>
                                            <td>{group.description || '-'}</td>
                                            <td>
                                                {group.propertyCount} {group.propertyCount === 1 ? 'property' : 'properties'}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${group.isActive ? 'active' : 'inactive'}`}>
                                                    {group.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <button 
                                                    onClick={() => openPropertiesModal(group)}
                                                    className="btn-sm btn-secondary"
                                                    title="Manage properties in this group"
                                                >
                                                    Properties
                                                </button>
                                                <button 
                                                    onClick={() => startEdit(group)}
                                                    className="btn-sm btn-secondary"
                                                    style={{ marginLeft: '4px' }}
                                                >
                                                    Edit
                                                </button>
                                                {group.isActive && (
                                                    <button 
                                                        onClick={() => handleDeleteGroup(group.propertyGroupID)}
                                                        className="btn-sm btn-danger"
                                                        style={{ marginLeft: '4px' }}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                )}
            </div>

            {/* Properties Modal */}
            {showPropertiesModal && selectedGroup && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Manage Properties - {selectedGroup.groupName}</h2>
                            <button onClick={() => setShowPropertiesModal(false)} className="close-btn">×</button>
                        </div>
                        <div className="modal-body">
                            <input
                                type="text"
                                placeholder="Search properties..."
                                value={propertySearchTerm}
                                onChange={(e) => setPropertySearchTerm(e.target.value)}
                                className="search-input"
                                style={{ marginBottom: '16px' }}
                            />
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {filteredAvailableProperties.map((property) => {
                                    const isSelected = selectedGroupProperties.some(p => p.propertyID === property.propertyID);
                                    return (
                                        <div key={property.propertyID} className="property-item">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleProperty(property)}
                                                />
                                                <span style={{ marginLeft: '8px' }}>
                                                    {property.propertyName || property.PropertyName}
                                                    {(property.comparativeName || property.ComparativeName) && 
                                                        ` (${property.comparativeName || property.ComparativeName})`
                                                    }
                                                </span>
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="modal-summary">
                                <strong>{selectedGroupProperties.length}</strong> properties selected
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button onClick={handleSaveGroupProperties} className="btn-primary">
                                Save Properties
                            </button>
                            <button onClick={() => setShowPropertiesModal(false)} className="btn-secondary">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Template Modal */}
            {showTemplateModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Create Group from Template</h2>
                            <button onClick={() => setShowTemplateModal(false)} className="close-btn">×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Select Template *</label>
                                <select
                                    value={selectedTemplate?.templateID || ''}
                                    onChange={(e) => {
                                        const template = templates.find(t => t.templateID === parseInt(e.target.value));
                                        setSelectedTemplate(template);
                                        if (template && template.description && !formData.description) {
                                            setFormData({ ...formData, description: template.description });
                                        }
                                    }}
                                    className="select-input"
                                >
                                    <option value="">-- Select a template --</option>
                                    {templates.map((t) => (
                                        <option key={t.templateID} value={t.templateID}>
                                            {t.templateName}
                                            {t.isSystemTemplate && ' (System)'}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedTemplate && (
                                <div className="template-info">
                                    <p><strong>Description:</strong> {selectedTemplate.description || 'No description'}</p>
                                </div>
                            )}

                            <div className="form-group">
                                <label>Group Name *</label>
                                <input
                                    type="text"
                                    value={formData.groupName}
                                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                                    placeholder="Enter a name for the new group"
                                />
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Optional description"
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button onClick={handleCreateFromTemplate} className="btn-primary">
                                Create from Template
                            </button>
                            <button onClick={() => setShowTemplateModal(false)} className="btn-secondary">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyGroupsPage;
